import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Plus, Search, MoreHorizontal, Check, Download, Eye, EyeOff } from 'lucide-react';
import { api } from '@/api/client';
import { Monogram } from '@/components/Monogram';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { BellDropdown } from '@/components/BellDropdown';
import { AvatarMenu } from '@/components/AvatarMenu';
import { Spinner } from '@/components/Spinner';
import { toast } from '@/state/toast';
import type { Role } from '@/types/api';

type Section = 'users' | 'tenants' | 'projects' | 'consultations' | 'audit' | 'flags';

interface AdminUser {
  id: string;
  email: string;
  /** Может прийти full_name или name — поддерживаем оба варианта */
  full_name?: string;
  name?: string;
  default_role: string;
  expert_section?: string | null;
  is_active?: boolean;
  expertise_id?: string | null;
  expertise_name?: string | null;
  expertise_code?: string | null;
  created_at: string;
}
interface Expertise {
  id: string;
  name: string;
  code: string | null;
  created_at: string;
}

export default function Admin() {
  const nav = useNavigate();
  const [section, setSection] = useState<Section>('users');

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-border" style={{ height: 48 }}>
        <div className="mx-auto h-full flex items-center" style={{ maxWidth: 1280, paddingLeft: 24, paddingRight: 24, gap: 16 }}>
          <div className="flex items-center" style={{ gap: 10 }}>
            <Monogram size={28} fontSize={16} />
            <span className="font-heading text-ink" style={{ fontSize: 15, fontWeight: 600 }}>Коллегиум · Админ-панель</span>
          </div>
          <div className="flex-1" />
          <Button
            variant="secondary"
            size="sm"
            icon={<ArrowRight size={14} style={{ transform: 'rotate(180deg)' }} />}
            onClick={() => nav('/projects')}
          >В кабинет</Button>
          <BellDropdown />
          <AvatarMenu />
        </div>
      </header>

      {/* Section tabs */}
      <div className="bg-surface border-b border-border" style={{ padding: '16px 24px' }}>
        <div className="mx-auto flex" style={{ maxWidth: 1280, gap: 4, flexWrap: 'wrap' }}>
          {([
            { id: 'users',         label: 'Пользователи' },
            { id: 'tenants',       label: 'Экспертизы' },
            { id: 'projects',      label: 'Проекты' },
            { id: 'consultations', label: 'Консультации' },
            { id: 'audit',         label: 'Аудит' },
            { id: 'flags',         label: 'Параметры' },
          ] as const).map((s) => {
            const active = section === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSection(s.id)}
                className="rounded-btn"
                style={{
                  padding: '8px 14px',
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  color: active ? '#0A1628' : '#6B7280',
                  background: active ? '#FFFFFF' : 'transparent',
                  border: active ? '1px solid #E5E7EB' : '1px solid transparent',
                  cursor: 'pointer',
                }}
              >{s.label}</button>
            );
          })}
        </div>
      </div>

      <main className="mx-auto" style={{ maxWidth: 1280, padding: 24 }}>
        {section === 'users'         && <UsersPanel />}
        {section === 'tenants'       && <TenantsPanel />}
        {section === 'projects'      && <ProjectsAdminPanel />}
        {section === 'consultations' && <ConsultationsPanel />}
        {section === 'audit'         && <AuditPanel />}
        {section === 'flags'         && <FlagsPanel />}
      </main>
    </div>
  );
}

/* ─── Users ────────────────────────────────────────────────────────────── */
const ROLE_COLOR: Record<Role, string> = { curator: '#F59E0B', expert: '#0A1628', gip: '#6B7280', admin: '#EF4444', customer: '#2563EB' };
const ROLE_LABEL: Record<Role, string> = { curator: 'Куратор', expert: 'Эксперт', gip: 'ГИП', admin: 'Админ', customer: 'Заказчик' };

function UsersPanel() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [resetPwUser, setResetPwUser] = useState<AdminUser | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const { data, isLoading, isError } = useQuery<{ users: AdminUser[] }>({
    queryKey: ['admin-users'],
    queryFn: () => api.get('/users'),
    retry: false,
  });
  const users = data?.users || [];
  const filtered = users.filter((u) => {
    const fullName = (u.full_name || u.name || '').toLowerCase();
    return !q || fullName.includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
        <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>Пользователи системы</h3>
        <Button variant="primary" icon={<Plus size={16} />} onClick={() => setShowCreate(true)}>Создать пользователя</Button>
      </div>
      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['admin-users'] }); }}
        />
      )}
      <div className="flex items-center mb-4" style={{ gap: 12 }}>
        <div className="relative" style={{ flex: '0 0 320px' }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по имени или эл. почте"
            className="w-full bg-white rounded-btn outline-none focus:border-ink"
            style={{ padding: '8px 12px 8px 36px', border: '1px solid #E5E7EB', fontSize: 13 }}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
        <div className="grid grid-cols-12 text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600, paddingBottom: 8, borderBottom: '1px solid #E5E7EB', gap: 12 }}>
          <div className="col-span-4">Имя · Эл. почта</div>
          <div className="col-span-2">Роль</div>
          <div className="col-span-3">Экспертиза</div>
          <div className="col-span-2">Создан</div>
          <div className="col-span-1 text-right">⋯</div>
        </div>
        {isLoading && <div style={{ padding: '32px 0', textAlign: 'center' }}><Spinner size={24} className="text-muted" /></div>}
        {isError && <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 14 }}>Доступ к /users только для admin. Залогиньтесь как admin.</p>}
        {!isLoading && !isError && filtered.length === 0 && <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 14 }}>{q ? `Никто не найден по «${q}»` : 'Пользователей нет'}</p>}
        {filtered.map((u) => {
          const fullName = u.full_name || u.name || u.email;
          const initials = fullName.split(/\s+/).filter(Boolean).slice(0, 2).map((s: string) => s[0]?.toUpperCase()).join('');
          const roleKey = (u.default_role || '').toLowerCase() as Role;
          return (
            <div key={u.id} className="grid grid-cols-12 items-center" style={{ padding: '12px 0', borderBottom: '1px solid #E5E7EB', fontSize: 13, gap: 12 }}>
              <div className="col-span-4 flex items-center" style={{ gap: 10 }}>
                <span className="rounded-pill bg-surface flex items-center justify-center tabular" style={{ width: 32, height: 32, fontSize: 11, fontWeight: 600 }}>{initials}</span>
                <div>
                  <div className="text-ink" style={{ fontWeight: 600 }}>{fullName}</div>
                  <div className="text-muted mt-0.5" style={{ fontSize: 12 }}>{u.email}</div>
                </div>
              </div>
              <div className="col-span-2">
                <span className="rounded-pill" style={{ padding: '2px 10px', fontSize: 12, fontWeight: 500, color: ROLE_COLOR[roleKey] || '#6B7280', background: '#F3F4F6', border: '1px solid #E5E7EB' }}>
                  {ROLE_LABEL[roleKey] || u.default_role}
                </span>
              </div>
              <div className="col-span-3 text-muted">{u.expertise_name || '—'}</div>
              <div className="col-span-2 text-muted tabular">{u.created_at ? new Date(u.created_at).toLocaleDateString('ru-RU') : '—'}</div>
              <div className="col-span-1 text-right" style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="text-muted"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === u.id ? null : u.id); }}
                >
                  <MoreHorizontal size={16} />
                </button>
                {openMenuId === u.id && (
                  <UserActionsMenu
                    user={u}
                    onClose={() => setOpenMenuId(null)}
                    onEdit={() => { setEditingUser(u); setOpenMenuId(null); }}
                    onResetPassword={() => { setResetPwUser(u); setOpenMenuId(null); }}
                    onAfterChange={() => qc.invalidateQueries({ queryKey: ['admin-users'] })}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); qc.invalidateQueries({ queryKey: ['admin-users'] }); }}
        />
      )}
      {resetPwUser && (
        <ResetPasswordModal
          user={resetPwUser}
          onClose={() => setResetPwUser(null)}
        />
      )}
    </div>
  );
}

/* Меню «...»: Редактировать / Сменить пароль / Изменить роль / Деактивировать-Активировать */
function UserActionsMenu({
  user, onClose, onEdit, onResetPassword, onAfterChange,
}: {
  user: AdminUser;
  onClose: () => void;
  onEdit: () => void;
  onResetPassword: () => void;
  onAfterChange: () => void;
}) {
  const setRoleMut = useMutation({
    mutationFn: (role: Role) => api.post('/auth/set-role', { user_id: user.id, role }),
    onSuccess: () => { toast.success('Роль изменена'); onAfterChange(); onClose(); },
    onError: (e) => toast.error((e as Error).message || 'Ошибка'),
  });
  const toggleActiveMut = useMutation({
    mutationFn: () => api.patch(`/auth/users/${user.id}`, { is_active: !(user.is_active ?? true) }),
    onSuccess: () => {
      toast.success((user.is_active ?? true) ? 'Пользователь деактивирован' : 'Активирован');
      onAfterChange(); onClose();
    },
    onError: (e) => toast.error((e as Error).message || 'Ошибка'),
  });

  const roles: { value: Role; label: string }[] = [
    { value: 'admin',    label: 'Админ' },
    { value: 'curator',  label: 'Куратор' },
    { value: 'gip',      label: 'ГИП' },
    { value: 'expert',   label: 'Эксперт' },
    { value: 'customer', label: 'Заказчик' },
  ];

  return (
    <>
      <div className="fixed inset-0" style={{ zIndex: 49 }} onClick={onClose} />
      <div
        style={{
          position: 'absolute', right: 0, top: 28, zIndex: 50,
          minWidth: 220, background: 'white', borderRadius: 8,
          border: '1px solid #E5E7EB', boxShadow: '0 8px 24px rgba(0,0,0,.08)',
          padding: 6, textAlign: 'left',
        }}
      >
      <button
        onClick={onEdit}
        className="w-full text-left rounded-btn"
        style={{ padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: '#0A1628' }}
      >Редактировать ФИО / экспертизу</button>
      <button
        onClick={onResetPassword}
        className="w-full text-left rounded-btn"
        style={{ padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: '#0A1628' }}
      >Сменить пароль</button>

      <div style={{ borderTop: '1px solid #F1F5F9', margin: '4px 0' }} />
      <div className="text-muted" style={{ fontSize: 10, padding: '8px 12px 4px', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Сменить роль</div>
      {roles.map(r => (
        <button
          key={r.value}
          onClick={() => setRoleMut.mutate(r.value)}
          disabled={setRoleMut.isPending || (user.default_role || '').toLowerCase() === r.value}
          className="w-full text-left rounded-btn"
          style={{
            padding: '6px 12px', background: 'transparent', border: 'none',
            cursor: 'pointer', fontSize: 13,
            color: (user.default_role || '').toLowerCase() === r.value ? '#94A3B8' : '#0A1628',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {(user.default_role || '').toLowerCase() === r.value && <Check size={12} />}
          {r.label}
        </button>
      ))}

      <div style={{ borderTop: '1px solid #F1F5F9', margin: '4px 0' }} />
      <button
        onClick={() => toggleActiveMut.mutate()}
        disabled={toggleActiveMut.isPending}
        className="w-full text-left rounded-btn"
        style={{ padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: (user.is_active ?? true) ? '#DC2626' : '#16A34A' }}
      >{(user.is_active ?? true) ? 'Деактивировать' : 'Активировать'}</button>
    </div>
    </>
  );
}

/* Редактирование ФИО, expert_section, expertise_id */
function EditUserModal({
  user, onClose, onSaved,
}: { user: AdminUser; onClose: () => void; onSaved: () => void }) {
  const [fullName, setFullName] = useState(user.full_name || user.name || '');
  const [expertSection, setExpertSection] = useState(user.expert_section || '');
  // '' = не менять; '__null__' = явно снять; UUID = назначить
  const [expertiseId, setExpertiseId] = useState<string>(user.expertise_id || '__null__');

  const { data: expData } = useQuery<{ expertises: Expertise[] }>({
    queryKey: ['expertises-list'],
    queryFn: () => api.get('/expertises'),
    staleTime: 30_000,
  });
  const expertises = expData?.expertises || [];

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = {
        full_name: fullName.trim() || null,
        expert_section: expertSection.trim() || null,
      };
      if (expertiseId === '__null__') {
        patch.expertise_id_null = true;
      } else {
        patch.expertise_id = expertiseId;
      }
      return api.patch(`/auth/users/${user.id}`, patch);
    },
    onSuccess: () => { toast.success('Сохранено'); onSaved(); },
    onError: (e) => toast.error((e as Error).message || 'Ошибка'),
  });

  return (
    <Modal title="Редактировать пользователя" onClose={onClose} maxWidth={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4, fontWeight: 500 }}>Email (нельзя изменить)</div>
          <input value={user.email} disabled style={{ width: '100%', padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 14, background: '#F8FAFC', color: '#64748B' }} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4, fontWeight: 500 }}>ФИО</div>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Иванов Иван Иванович" style={{ width: '100%', padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 14 }} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4, fontWeight: 500 }}>Организация / экспертиза</div>
          <select
            value={expertiseId}
            onChange={(e) => setExpertiseId(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 14, background: '#FFFFFF' }}
          >
            <option value="__null__">— не назначена —</option>
            {expertises.map((ex) => (
              <option key={ex.id} value={ex.id}>{ex.name}{ex.code ? ` (${ex.code})` : ''}</option>
            ))}
          </select>
          {expertises.length === 0 && (
            <p style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
              Сначала создайте организации в разделе «Экспертизы»
            </p>
          )}
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4, fontWeight: 500 }}>Раздел эксперта <span style={{ fontWeight: 400 }}>(ТКР, ПОС, ООС… — для роли «Эксперт»)</span></div>
          <input value={expertSection} onChange={(e) => setExpertSection(e.target.value)} placeholder="ТКР, ПОС, ООС…" style={{ width: '100%', padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 14 }} />
        </div>
      </div>
    </Modal>
  );
}

function CreateUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('expert');
  const [password, setPassword] = useState('');
  const [expertiseId, setExpertiseId] = useState<string>('__null__');
  const [err, setErr] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const { data: expData } = useQuery<{ expertises: Expertise[] }>({
    queryKey: ['expertises-list'],
    queryFn: () => api.get('/expertises'),
    staleTime: 30_000,
  });
  const expertises = expData?.expertises || [];

  const mutation = useMutation({
    mutationFn: async (payload: { name: string; email: string; default_role: string; password?: string }) => {
      const data = await api.post<{ id: string; temp_password?: string }>('/users', payload);
      // Если выбрана экспертиза — назначить сразу
      if (expertiseId !== '__null__' && data.id) {
        await api.patch(`/auth/users/${data.id}`, { expertise_id: expertiseId });
      }
      return data;
    },
    onSuccess: (data) => {
      if (data.temp_password) {
        setTempPassword(data.temp_password);
      } else {
        toast.success('Пользователь создан');
        onCreated();
      }
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Не удалось'),
  });

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const passwordOk = password.trim().length === 0 || password.trim().length >= 8;
  const canSubmit = trimmedName.length >= 1 && emailValid && passwordOk && !mutation.isPending;

  const onSubmit = (ev?: React.FormEvent) => {
    ev?.preventDefault();
    if (!canSubmit) return;
    setErr(null);
    const payload: { name: string; email: string; default_role: string; password?: string } = {
      name: trimmedName,
      email: trimmedEmail,
      default_role: role,
    };
    if (password.trim().length >= 8) payload.password = password.trim();
    mutation.mutate(payload);
  };

  // Показываем временный пароль после создания
  if (tempPassword) {
    return (
      <Modal title="Пользователь создан" onClose={() => { onCreated(); }} maxWidth={520}
        footer={<Button variant="primary" onClick={() => { onCreated(); }}>Готово</Button>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 14, color: '#0A1628' }}>
            Аккаунт создан. Передайте пользователю временный пароль для первого входа:
          </p>
          <div style={{ background: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: 8, padding: '12px 16px', fontFamily: 'monospace', fontSize: 18, fontWeight: 700, letterSpacing: 2, color: '#0A1628', userSelect: 'all', textAlign: 'center' }}>
            {tempPassword}
          </div>
          <p className="text-muted" style={{ fontSize: 12 }}>
            Этот пароль показывается только один раз. Пользователь сможет сменить его в настройках.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Новый пользователь"
      onClose={mutation.isPending ? () => {} : onClose}
      maxWidth={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>Отмена</Button>
          <Button variant="primary" onClick={() => onSubmit()} disabled={!canSubmit}>{mutation.isPending ? 'Создание…' : 'Создать'}</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <label className="block">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>ФИО</span>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setErr(null); }}
            placeholder="Иванов Сергей Петрович"
            autoFocus
            maxLength={200}
            className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
            style={{ padding: '10px 14px', fontSize: 14 }}
          />
        </label>
        <label className="block mt-3">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>Эл. почта</span>
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErr(null); }}
            placeholder="ivanov@example.org"
            maxLength={320}
            className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
            style={{ padding: '10px 14px', fontSize: 14 }}
          />
        </label>
        <label className="block mt-3">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>Роль по умолчанию</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
            style={{ padding: '10px 14px', fontSize: 14 }}
          >
            <option value="curator">Куратор</option>
            <option value="expert">Эксперт</option>
            <option value="gip">ГИП</option>
            <option value="admin">Админ</option>
            <option value="customer">Заказчик</option>
          </select>
        </label>
        <label className="block mt-3">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>Организация / экспертиза</span>
          <select
            value={expertiseId}
            onChange={(e) => setExpertiseId(e.target.value)}
            className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
            style={{ padding: '10px 14px', fontSize: 14 }}
          >
            <option value="__null__">— не назначать —</option>
            {expertises.map((ex) => (
              <option key={ex.id} value={ex.id}>{ex.name}{ex.code ? ` (${ex.code})` : ''}</option>
            ))}
          </select>
          {expertises.length === 0 && (
            <p className="text-muted mt-1" style={{ fontSize: 11 }}>
              Создайте организации в разделе «Экспертизы»
            </p>
          )}
        </label>
        <label className="block mt-3">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>
            Пароль <span className="text-muted" style={{ fontWeight: 400 }}>(оставьте пустым — сгенерируем автоматически)</span>
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setErr(null); }}
            placeholder="Минимум 8 символов"
            maxLength={256}
            className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
            style={{ padding: '10px 14px', fontSize: 14, borderColor: password.trim().length > 0 && password.trim().length < 8 ? '#EF4444' : undefined }}
          />
          {password.trim().length > 0 && password.trim().length < 8 && (
            <p style={{ fontSize: 11, color: '#EF4444', marginTop: 4 }}>Минимум 8 символов</p>
          )}
        </label>
        {err && (
          <div className="rounded-btn mt-4" style={{ padding: '10px 12px', background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', fontSize: 13, color: '#B91C1C' }} role="alert">{err}</div>
        )}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
      </form>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (pw: string) => api.post('/auth/admin-set-password', { user_id: user.id, new_password: pw }),
    onSuccess: () => setDone(true),
    onError: (e) => setErr(e instanceof Error ? e.message : 'Ошибка'),
  });

  const trimmed = password.trim();
  const canSubmit = trimmed.length >= 8 && !mutation.isPending;

  if (done) {
    return (
      <Modal title="Пароль изменён" onClose={onClose} maxWidth={480}
        footer={<Button variant="primary" onClick={onClose}>Закрыть</Button>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 14, color: '#0A1628' }}>
            Пароль для <strong>{user.full_name || user.name || user.email}</strong> успешно изменён.
          </p>
          <div style={{ padding: '10px 14px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6 }}>
            <span style={{ fontSize: 11, color: '#64748B', display: 'block', marginBottom: 4 }}>Установленный пароль:</span>
            <span style={{ fontSize: 14, fontFamily: 'monospace', fontWeight: 600, color: '#0A1628', letterSpacing: '0.05em' }}>{password}</span>
          </div>
          <p className="text-muted" style={{ fontSize: 12 }}>
            Все активные сессии пользователя завершены — при следующем входе потребуется новый пароль.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Сменить пароль" onClose={onClose} maxWidth={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>Отмена</Button>
          <Button variant="primary" onClick={() => mutation.mutate(trimmed)} disabled={!canSubmit}>
            {mutation.isPending ? 'Сохраняем…' : 'Установить пароль'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ fontSize: 13, color: '#64748B' }}>
          Новый пароль для: <strong style={{ color: '#0A1628' }}>{user.full_name || user.name || user.email}</strong>
        </p>
        <div style={{ position: 'relative' }}>
          <input
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setErr(null); }}
            placeholder="Минимум 8 символов"
            autoFocus
            autoComplete="new-password"
            maxLength={256}
            style={{ width: '100%', padding: '10px 42px 10px 14px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            tabIndex={-1}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 0, lineHeight: 1 }}
            title={showPw ? 'Скрыть пароль' : 'Показать пароль'}
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {trimmed.length > 0 && trimmed.length < 8 && (
          <p style={{ fontSize: 11, color: '#EF4444' }}>Минимум 8 символов</p>
        )}
        {err && (
          <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 6, fontSize: 13, color: '#B91C1C' }}>{err}</div>
        )}
      </div>
    </Modal>
  );
}

/* ─── Tenants ────────────────────────────────────────────────────────── */
interface ExpertiseRow extends Expertise { is_active?: boolean; members_count?: number }

function TenantsPanel() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const { data, isLoading, isError } = useQuery<{ expertises: ExpertiseRow[] }>({
    queryKey: ['admin-tenants'],
    queryFn: () => api.get('/expertises'),
    retry: false,
  });
  const tenants = data?.expertises || [];

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
        <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>Организации-экспертизы</h3>
        <Button variant="primary" icon={<Plus size={16} />} onClick={() => setShowCreate(true)}>Создать экспертизу</Button>
      </div>
      {isLoading && <Spinner size={24} className="text-muted" />}
      {isError && <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 14 }}>Доступ к /expertises только для admin</p>}
      {!isLoading && !isError && tenants.length === 0 && <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 14 }}>Экспертиз пока нет</p>}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 }}>
        {tenants.map((t) => {
          const active = t.is_active !== false;
          return (
            <div key={t.id} className="bg-white rounded-card border border-border" style={{ padding: 20 }}>
              <div className="flex items-center justify-between">
                <span className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>{t.code || '—'}</span>
                <span
                  className="rounded-pill"
                  style={{
                    padding: '2px 10px', fontSize: 11, fontWeight: 600,
                    background: active ? 'rgba(16,185,129,.1)' : '#F3F4F6',
                    color: active ? '#10B981' : '#6B7280',
                  }}
                >{active ? 'активна' : 'неактивна'}</span>
              </div>
              <h4 className="font-heading text-ink mt-3" style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.3 }}>{t.name}</h4>
              <p className="text-muted mt-2" style={{ fontSize: 12 }}>
                ID: {t.id.slice(0, 8)} · создана {new Date(t.created_at).toLocaleDateString('ru-RU')}
                {typeof t.members_count === 'number' && (
                  <> · участников: <span className="tabular text-ink">{t.members_count}</span></>
                )}
              </p>
            </div>
          );
        })}
      </div>
      {showCreate && <CreateExpertiseModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['admin-tenants'] }); }} />}
    </div>
  );
}

function CreateExpertiseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (payload: { name: string; code: string | null; is_active: boolean }) =>
      api.post<{ id: string }>('/expertises', payload),
    onSuccess: () => { toast.success('Экспертиза создана'); onCreated(); },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Не удалось'),
  });
  const trimmed = name.trim();
  const canSubmit = trimmed.length >= 1 && !mutation.isPending;
  const onSubmit = (ev?: React.FormEvent) => {
    ev?.preventDefault();
    if (!canSubmit) return;
    setErr(null);
    mutation.mutate({ name: trimmed, code: code.trim() || null, is_active: isActive });
  };
  return (
    <Modal
      title="Новая экспертиза"
      onClose={mutation.isPending ? () => {} : onClose}
      maxWidth={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>Отмена</Button>
          <Button variant="primary" onClick={() => onSubmit()} disabled={!canSubmit}>{mutation.isPending ? 'Создание…' : 'Создать'}</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <label className="block">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>Название</span>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setErr(null); }}
            placeholder="Главгосэкспертиза по СЗФО"
            autoFocus
            maxLength={200}
            className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
            style={{ padding: '10px 14px', fontSize: 14 }}
          />
        </label>
        <label className="block mt-3">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>Код (опционально)</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="SZFO"
            maxLength={50}
            className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
            style={{ padding: '10px 14px', fontSize: 14 }}
          />
        </label>
        <label className="flex items-center mt-4" style={{ gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Активна</span>
        </label>
        {err && (
          <div className="rounded-btn mt-4" style={{ padding: '10px 12px', background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', fontSize: 13, color: '#B91C1C' }} role="alert">{err}</div>
        )}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
      </form>
    </Modal>
  );
}

/* ─── Projects (admin view) ──────────────────────────────────────────── */
interface ProjectAdminRow { id: string; name: string; status: string | null; curator_id: string | null }

function ProjectsAdminPanel() {
  const nav = useNavigate();
  const { data, isLoading } = useQuery<{ projects: ProjectAdminRow[] }>({
    queryKey: ['admin-projects'],
    queryFn: () => api.get('/projects'),
  });
  const list = data?.projects || [];
  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
        <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600 }}>Все проекты</h3>
        <Button variant="primary" icon={<ArrowRight size={16} />} onClick={() => nav('/projects')} title="Создание/редактирование проектов — на экране «Проекты» в кабинете">Перейти к проектам</Button>
      </div>
      {isLoading && <Spinner size={24} className="text-muted" />}
      <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
        <div className="grid grid-cols-12 text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600, paddingBottom: 8, borderBottom: '1px solid #E5E7EB', gap: 12 }}>
          <div className="col-span-2">ID</div>
          <div className="col-span-6">Название</div>
          <div className="col-span-2">Статус</div>
          <div className="col-span-2 text-right">Куратор</div>
        </div>
        {list.slice(0, 20).map((p) => (
          <div key={p.id} className="grid grid-cols-12 items-center" style={{ padding: '12px 0', borderBottom: '1px solid #E5E7EB', fontSize: 13, gap: 12 }}>
            <div className="col-span-2 tabular text-muted">{p.id.slice(0, 8)}</div>
            <div className="col-span-6 text-ink" style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.name}</div>
            <div className="col-span-2">
              <span className="rounded-pill bg-surface" style={{ padding: '2px 10px', fontSize: 11 }}>{p.status || 'активен'}</span>
            </div>
            <div className="col-span-2 text-muted text-right tabular">{p.curator_id?.slice(0, 8) || '—'}</div>
          </div>
        ))}
        {list.length > 20 && <p className="text-muted text-center mt-3" style={{ fontSize: 12 }}>Показано 20 из {list.length}</p>}
      </div>
    </div>
  );
}

/* ─── Consultations (15.C — инбокс координатора) ─────────────────────── */
// Запросы платных консультаций от заказчиков. Координатор (admin) видит
// контекст находки, вопрос заказчика и меняет статус запроса.
// GET /consultations?only_new=  · POST /consultations/{id}/status
type ConsultationStatus = 'new' | 'quoted' | 'answered' | 'closed';
interface ConsultationRow {
  id: string;
  project_id: string;
  project_name: string | null;
  requester_user_id: string | null;
  status: ConsultationStatus;
  check_type: string | null;
  ref: string | null;
  verdict: string | null;
  norm: string | null;
  citation: string | null;
  document_id: string | null;
  page: number | null;
  question: string;
  // 15.C: вложения (выбранные заказчиком замечания/находки) — контекст заявки.
  attachments?: Array<{ kind?: string; title?: string; ref?: string; norm?: string; verdict?: string; citation?: string; document_title?: string; page?: number }>;
  created_at: string;
}

const CONSULT_STATUS_LABEL: Record<ConsultationStatus, string> = {
  new: 'Новый', quoted: 'В работе', answered: 'Отвечено', closed: 'Закрыт',
};
const CONSULT_STATUS_COLOR: Record<ConsultationStatus, { bg: string; fg: string }> = {
  new:      { bg: 'rgba(245,158,11,.12)', fg: '#B45309' },
  quoted:   { bg: 'rgba(37,99,235,.10)',  fg: '#2563EB' },
  answered: { bg: 'rgba(16,185,129,.10)', fg: '#047857' },
  closed:   { bg: '#F3F4F6',              fg: '#6B7280' },
};

function ConsultationsPanel() {
  const qc = useQueryClient();
  const [onlyNew, setOnlyNew] = useState(false);
  const { data, isLoading, isError } = useQuery<ConsultationRow[]>({
    queryKey: ['admin-consultations', onlyNew],
    queryFn: () => api.get<ConsultationRow[]>(`/consultations?only_new=${onlyNew}`),
    retry: false,
  });
  const rows = data || [];

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ConsultationStatus }) =>
      api.post(`/consultations/${id}/status`, { status }),
    onSuccess: () => {
      toast.success('Статус обновлён');
      qc.invalidateQueries({ queryKey: ['admin-consultations'] });
    },
    onError: (e) => toast.error((e as Error).message || 'Не удалось обновить статус'),
  });

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>Запросы консультаций</h3>
        <div className="flex" style={{ gap: 4 }}>
          {([
            { id: false, label: 'Все' },
            { id: true,  label: 'Только новые' },
          ] as const).map((f) => {
            const active = onlyNew === f.id;
            return (
              <button
                key={String(f.id)}
                type="button"
                onClick={() => setOnlyNew(f.id)}
                className="rounded-btn"
                style={{
                  padding: '6px 12px', fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? '#0A1628' : '#6B7280',
                  background: active ? '#FFFFFF' : 'transparent',
                  border: active ? '1px solid #E5E7EB' : '1px solid transparent',
                  cursor: 'pointer',
                }}
              >{f.label}</button>
            );
          })}
        </div>
      </div>

      {isLoading && <div style={{ padding: '32px 0', textAlign: 'center' }}><Spinner size={24} className="text-muted" /></div>}
      {isError && <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 14 }}>Доступ к /consultations только для admin. Залогиньтесь как admin.</p>}
      {!isLoading && !isError && rows.length === 0 && (
        <div className="bg-white rounded-card border border-border text-muted text-center" style={{ padding: '48px 24px', fontSize: 14 }}>
          Запросов консультаций пока нет
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {rows.map((c) => (
          <ConsultationCard
            key={c.id}
            row={c}
            busy={setStatus.isPending}
            onSetStatus={(status) => setStatus.mutate({ id: c.id, status })}
          />
        ))}
      </div>
    </div>
  );
}

function ConsultationCard({
  row, busy, onSetStatus,
}: {
  row: ConsultationRow;
  busy: boolean;
  onSetStatus: (status: ConsultationStatus) => void;
}) {
  const color = CONSULT_STATUS_COLOR[row.status];
  // Действия перевода статуса (глаголом), скрываем те, что совпадают с текущим.
  const actions: { status: ConsultationStatus; label: string }[] = [
    { status: 'quoted',   label: 'Взять в работу' },
    { status: 'answered', label: 'Отвечено' },
    { status: 'closed',   label: 'Закрыть' },
  ];
  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 20 }}>
      <div className="flex items-start" style={{ gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="flex items-center" style={{ gap: 10, flexWrap: 'wrap' }}>
            <span className="font-heading text-ink" style={{ fontSize: 15, fontWeight: 600 }}>
              {row.project_name || 'Проект без названия'}
            </span>
            <span className="rounded-pill" style={{ padding: '2px 10px', fontSize: 11, fontWeight: 600, background: color.bg, color: color.fg }}>
              {CONSULT_STATUS_LABEL[row.status]}
            </span>
            <span className="text-muted tabular" style={{ fontSize: 12 }}>
              {new Date(row.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>

      {/* Контекст находки */}
      {(row.verdict || row.ref || row.norm || row.citation || row.check_type || row.page != null) && (
        <div className="rounded-btn" style={{ marginTop: 12, padding: 12, background: '#F8FAFC', border: '1px solid #E5E7EB' }}>
          <div className="flex" style={{ gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
            {row.check_type && <span className="text-muted">Проверка: <span className="text-ink" style={{ fontWeight: 500 }}>{row.check_type}</span></span>}
            {row.verdict && <span className="text-muted">Вердикт: <span className="text-ink" style={{ fontWeight: 500 }}>{row.verdict}</span></span>}
            {row.ref && <span className="text-muted">Пункт: <span className="text-ink" style={{ fontWeight: 500 }}>{row.ref}</span></span>}
            {row.norm && <span className="text-muted">Норма: <span className="text-ink" style={{ fontWeight: 500 }}>{row.norm}</span></span>}
            {row.page != null && <span className="text-muted">Стр.: <span className="text-ink tabular" style={{ fontWeight: 500 }}>{row.page}</span></span>}
          </div>
          {row.citation && (
            <div className="text-muted" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5, fontStyle: 'italic' }}>
              «{row.citation}»
            </div>
          )}
        </div>
      )}

      {/* 15.C: вложения — прикреплённые заказчиком замечания/находки. */}
      {row.attachments && row.attachments.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 600, marginBottom: 6 }}>
            Прикреплено · {row.attachments.length}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {row.attachments.map((a, i) => (
              <div key={i} className="rounded-btn" style={{ padding: '8px 12px', background: '#F8FAFC', border: '1px solid #E5E7EB' }}>
                <div className="text-ink" style={{ fontSize: 12.5, lineHeight: 1.4 }}>{a.title || a.ref || 'Вложение'}</div>
                {(a.ref || a.norm || a.verdict) && (
                  <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {[a.ref, a.norm, a.verdict].filter(Boolean).join(' · ')}
                  </div>
                )}
                {a.citation && (
                  <div className="text-muted" style={{ fontSize: 11.5, marginTop: 4, fontStyle: 'italic', lineHeight: 1.5 }}>«{a.citation}»</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Вопрос заказчика */}
      <div style={{ marginTop: 12 }}>
        <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Вопрос заказчика</div>
        <div className="text-ink" style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{row.question}</div>
      </div>

      {/* Смена статуса */}
      <div className="flex items-center" style={{ marginTop: 16, gap: 8, flexWrap: 'wrap' }}>
        {actions.filter((a) => a.status !== row.status).map((a) => (
          <Button
            key={a.status}
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => onSetStatus(a.status)}
          >{a.label}</Button>
        ))}
      </div>
    </div>
  );
}

/* ─── Audit ──────────────────────────────────────────────────────────── */
interface AuditEvent { id: string; event_type: string; payload: Record<string, unknown>; created_at: string; actor_email: string | null; ip: string | null }
function AuditPanel() {
  const { data, isLoading, isError } = useQuery<{ events: AuditEvent[] }>({
    queryKey: ['admin-audit'],
    queryFn: () => api.get('/audit-events?limit=50'),
    retry: false,
  });
  const events = data?.events || [];
  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
        <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600 }}>Системный аудит</h3>
        <span title="Появится в следующих версиях" style={{ display: 'inline-block' }}>
          <Button variant="secondary" icon={<Download size={14} />} disabled>Экспорт CSV</Button>
        </span>
      </div>
      {isLoading && <Spinner size={24} className="text-muted" />}
      {isError && <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 14 }}>Доступ к /audit-events только для admin</p>}
      <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
        {events.length === 0 && !isLoading && !isError && <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 14 }}>Событий аудита пока нет</p>}
        {events.slice(0, 15).map((e) => (
          <div key={e.id} className="flex items-start" style={{ padding: '12px 0', borderBottom: '1px solid #E5E7EB', gap: 14 }}>
            <span className="text-muted tabular" style={{ fontSize: 12, minWidth: 100 }}>{new Date(e.created_at).toLocaleString('ru-RU')}</span>
            <div style={{ flex: 1 }}>
              <div className="text-ink" style={{ fontSize: 14, fontWeight: 500 }}>{e.event_type}</div>
              <div className="text-muted mt-0.5" style={{ fontSize: 12 }}>{e.actor_email || 'system'} {e.ip ? `· ${e.ip}` : ''}</div>
            </div>
            <span className="rounded-pill bg-surface text-muted" style={{ padding: '2px 10px', fontSize: 11 }}>{e.event_type.split('_')[0]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Flags ──────────────────────────────────────────────────────────── */
interface FeatureFlag { key: string; enabled: boolean; description?: string; updated_at?: string }

function FlagsPanel() {
  const { data, isLoading } = useQuery<{ flags: FeatureFlag[] }>({
    queryKey: ['admin-flags'],
    queryFn: async () => {
      const r = await api.get<unknown>('/meta/feature-flags');
      // Backend может вернуть {flags:[]} | dict. Нормализуем.
      const obj = r as { flags?: FeatureFlag[] };
      if (obj.flags) return { flags: obj.flags };
      const dict = r as Record<string, FeatureFlag>;
      return { flags: Object.values(dict) };
    },
    retry: false,
  });
  const flags = data?.flags || [];

  return (
    <div>
      <h3 className="font-heading text-ink" style={{ marginBottom: 20, fontSize: 20, fontWeight: 600 }}>Параметры системы</h3>
      <div className="grid grid-cols-12" style={{ gap: 16 }}>
        <div className="col-span-7">
          <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
            <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>Feature flags</h4>
            <p className="text-muted mt-1" style={{ fontSize: 13 }}>Runtime-параметры функциональности</p>
            {isLoading && <Spinner size={24} className="text-muted mt-4" />}
            <div className="mt-4">
              {flags.length === 0 && !isLoading && (
                <p className="text-muted text-center" style={{ padding: '16px 0', fontSize: 13 }}>Флаги не определены</p>
              )}
              {flags.map((f) => (
                <FlagRow key={f.key} flag={f} />
              ))}
            </div>
          </div>
        </div>
        <div className="col-span-5">
          <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
            <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>Состояние системы</h4>
            <p className="text-muted mt-1" style={{ fontSize: 13 }}>Версии критичных компонентов</p>
            <div className="mt-4">
              {[
                { k: 'Версия API', v: '1.0.0', ok: true },
                { k: 'PostgreSQL', v: '16.2',  ok: true },
                { k: 'Neo4j',      v: '5.x',   ok: true },
                { k: 'Chroma',     v: '0.5.23', ok: true },
              ].map((s) => (
                <div key={s.k} className="flex items-center justify-between" style={{ padding: '10px 0', borderBottom: '1px solid #E5E7EB', fontSize: 13 }}>
                  <div className="text-muted">{s.k}</div>
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <span className="text-ink tabular" style={{ fontWeight: 500 }}>{s.v}</span>
                    {s.ok && <Check size={14} stroke="#10B981" />}
                  </div>
                </div>
              ))}
            </div>
            <span title="Появится в следующих версиях" style={{ display: 'inline-block', marginTop: 16 }}>
              <Button variant="secondary" size="sm" icon={<Download size={14} />} disabled>Скачать diagnostic-bundle</Button>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FlagRow({ flag }: { flag: FeatureFlag }) {
  const [enabled, setEnabled] = useState(flag.enabled);
  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      await api.post(`/meta/feature-flags/${flag.key}`, { enabled: next });
      toast.info(`Флаг ${flag.key}: ${next ? 'включён' : 'выключен'}`);
    } catch (e) {
      setEnabled(!next);
      toast.error(e instanceof Error ? e.message : 'Не удалось');
    }
  };
  return (
    <div className="flex items-center justify-between" style={{ padding: '12px 0', borderBottom: '1px solid #E5E7EB' }}>
      <div style={{ flex: 1 }}>
        <div className="font-heading text-ink" style={{ fontSize: 14, fontWeight: 600 }}>{flag.key}</div>
        <div className="text-muted mt-0.5" style={{ fontSize: 12 }}>{flag.description || '—'}</div>
      </div>
      <button
        type="button"
        onClick={toggle}
        className="rounded-pill"
        style={{
          width: 40, height: 22, position: 'relative',
          background: enabled ? '#F59E0B' : '#E5E7EB',
          border: 'none', cursor: 'pointer', transition: 'background .15s',
        }}
        aria-label={`Переключить ${flag.key}`}
      >
        <span style={{ position: 'absolute', top: 3, left: enabled ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#FFFFFF', transition: 'left .15s' }} />
      </button>
    </div>
  );
}
